/**
 * Componente/Validators
 * 
 * @author Ronald J Llusco - 79746003<lluscovillarpando@gmail.com>
 * @version 1.0.0
 * @license MIT
 */
import { Injectable, signal } from '@angular/core';
import { AbstractControl, ValidationErrors, UntypedFormGroup } from '@angular/forms';
import { NgxImageCompressService } from 'ngx-image-compress';
import { Subject } from 'rxjs';
import { Company, GetUser, ListBranch } from '../auth/auth.interface';
import { inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { severity } from '../shared/interfaces/api.interfaces';
import { GetPointsOfSale } from './interfaces/parametric.interface';

@Injectable({
  providedIn: 'root'
})
export class ValidatorsService {
  emailPattern = signal('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$');
  numberCiPattern = signal('^(?:(?=[eE][-])([1-9][0-9]{3,7})|([eE][-])?[1-9][0-9]{3,7}|[1-9][0-9]{3,7}[-][1-9][0-9A-Za-z]?)$');
  loadingPage = signal(false);
  isPageViewMovil = signal(false);
  expRegValidInput: RegExp = /^[^<>*!$~@%^&`¡;+¥¿?=]/;

  decimalLength = signal(2);

  localCompressedURl: any;
  //localCompressedURl1: any;
  compressLoading: boolean = false;
  //compressLoading1: boolean = false;
  user = signal<GetUser | undefined>(undefined);
  company = signal<Company | undefined>(undefined);
  id_sucursal = signal(1);
  listBranch = signal<ListBranch[]>([]);
  pointsOfSales = signal<GetPointsOfSale[]>([]);
  reload = signal(false);
  reload_sucursal_storages$: Subject<boolean> = new Subject();
  messageService = inject(MessageService);
  constructor(private imageCompress: NgxImageCompressService) {

  }

  resetState() {
    this.user.set(undefined);
    this.company.set(undefined);
    this.id_sucursal.set(1);
    this.listBranch.set([]);
    this.pointsOfSales.set([]);
    this.reload.set(false);
  }

  // get getCompany() {
  //   return {...this.company()}
  // }

  inputValidMessage(campo: string, form: UntypedFormGroup, isNumber: boolean = false): string {
    const error = form.get(campo)?.errors;
    return this.messageError(error, isNumber);
  }

  inputValid(campo: string, form: UntypedFormGroup): boolean {
    return form.get(campo)?.invalid && form.get(campo)?.touched ? true : false;
  }

  messageError(error: any, isNumber: boolean): string {
    if (error?.hasOwnProperty('required')) {
      return 'El campo es requerido';
    }
    if (error?.hasOwnProperty('minlength')) {
      return `El mínimo de caracteres es ${error?.['minlength']?.requiredLength}`;
    }
    if (error?.hasOwnProperty('maxlength')) {
      return `El máximo de caracteres es ${error?.['maxlength']?.requiredLength}`;
    }
    if (error?.hasOwnProperty('pattern')) {
      return `El campo no es valido`;
    }
    if (error?.hasOwnProperty('min') && isNumber) {
      return `El cantidad minima es ${error?.['min']?.min}`;
    }
    if (error?.hasOwnProperty('max') && isNumber) {
      return `La cantidad maxima es de ${error?.['max']?.max}`;
    }
    if (Object.keys(error).length > 0) {
      return `El campo no es valido`;
    }
    return '';
  }

  isSpacesInDynamicTxt(control: AbstractControl): ValidationErrors | null {
    const txt = control.value;
    return !txt || txt.trim().length === 0 ? { isSpacesInTxt: true } : null;
  }

  isSpacesInPassword(control: AbstractControl): ValidationErrors | null {
    const txt = control.value;
    return !txt || txt.trim().length < 8 ? { isSpacesInPassword: true } : null;
  }

  cardNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;

    const cleanValue = value.toString().replace(/\s+/g, '');
    if (!/^\d{16}$/.test(cleanValue)) {
      return { invalidLength: true };
    }

    let sum = 0;
    let shouldDouble = false;
    for (let i = cleanValue.length - 1; i >= 0; i--) {
      let digit = parseInt(cleanValue.charAt(i));
      if (shouldDouble) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
      shouldDouble = !shouldDouble;
    }

    return (sum % 10 === 0) ? null : { luhnCheck: true };
  }

  compressFile(imageBase64: string, img: File): Promise<File> {
    const sizeImage = img.size;
    const filename = img['name'];
    if (sizeImage <= 3145728) {
      return new Promise((resolve, reject) => {
        this.localCompressedURl = imageBase64;
        this.compressLoading = false;
        resolve(img);
      });
    }
    let quality = sizeImage > 10485760 ? 30 : 50;
    return new Promise((resolve, reject) => {
      this.imageCompress.compressFile(imageBase64, -1, quality, quality).then(
        result => {
          this.localCompressedURl = result;
          const imageBlob = this.dataURItoBlob(result.split(',')[1]);
          const imageFile = new File([imageBlob], filename, { type: 'image/jpeg' });
          this.compressLoading = false;
          resolve(imageFile);
        }).catch(err => {
          this.compressLoading = false;
          reject(err);
        });
    })
  }

  dataURItoBlob(dataURI: string) {
    const byteString = window.atob(dataURI);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const int8Array = new Uint8Array(arrayBuffer); for (let i = 0; i < byteString.length; i++) {
      int8Array[i] = byteString.charCodeAt(i);
    } const blob = new Blob([int8Array], { type: 'image/jpeg' });
    return blob;
  }

  optimizeToWebP(file: File, quality: number = 0.8): Promise<File> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event: any) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          // Resize logic
          const MAX_WIDTH = 1920;
          const MAX_HEIGHT = 1080;
          if (width > height) {
            if (width > MAX_WIDTH) {
              height = Math.round(height * MAX_WIDTH / width);
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = Math.round(width * MAX_HEIGHT / height);
              height = MAX_HEIGHT;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob((blob) => {
              if (blob) {
                const newFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".webp", { type: 'image/webp' });
                // Store local preview if needed
                this.localCompressedURl = URL.createObjectURL(blob);
                this.compressLoading = false;
                resolve(newFile);
              } else {
                reject(new Error('Canvas to Blob failed'));
              }
            }, 'image/webp', quality);
          } else {
            reject(new Error('Canvas context failed'));
          }
        };
        img.onerror = (error) => reject(error);
      };
      reader.onerror = (error) => reject(error);
    });
  }

  showToast(severity: severity, title: string, detail: string, life: number = 4000) {
    let primeSeverity = severity as string;
    if (severity === 'danger') {
      primeSeverity = 'error';
    } else if (severity === 'warning') {
      primeSeverity = 'warn';
    }
    this.messageService.add({ severity: primeSeverity, summary: title, detail, life });
  }
}
